// Library methods
import { Navigate } from "react-router-dom";

// Components
import PortalLayout from "src/components/shared/layouts/PortalLayout/PortalLayout";
import ProtectedRoute from "src/components/shared/navigation/ProtectedRoute/ProtectedRoute";
import Callback from "src/pages/Callback/Callback";
import Settings from "src/pages/Settings/Settings";
import Dashboard from "src/pages/Dashboard/Dashboard";
import Invalid from "src/pages/Invalid/Invalid";
import NotFound from "src/pages/NotFound/NotFound";
import Patients from "src/pages/Patients/Patients";
import ExamReport from "src/pages/ExamReport/ExamReport";
import ProgressionAnalysisReport from "src/pages/ProgressionAnalysisReport/ProgressionAnalysisReport";
import UnauthorizedPdf from "src/pages/UnauthorizedPdf/UnauthorizedPdf";
import KnowledgeHub from "src/pages/KnowledgeHub/KnowledgeHub";
import Emr from "src/pages/Emr/Emr";
import PrivacyPolicy from "src/pages/PrivacyPolicy/PrivacyPolicy";
import TermsAndConditions from "src/pages/TermsAndConditions/TermsAndConditions";
import EmrConnections from "src/pages/EmrConnections/EmrConnections";
import EmrJobs from "src/pages/EmrJobs/EmrJobs";

const routes = [
  {
    path: "/",
    element: <Navigate to="/dashboard" />,
  },
  {
    path: "dashboard",
    element: <ProtectedRoute component={PortalLayout} />,
    children: [
      {
        path: "",
        element: <Dashboard />,
      },
    ],
  },
  {
    path: "data-hub",
    element: <ProtectedRoute component={PortalLayout} />,
    children: [
      {
        path: "",
        element: <Emr />,
      },
      {
        path: ":hubId/connections",
        element: <EmrConnections />,
      },
      {
        path: ":hubId/jobs",
        element: <EmrJobs />,
      },
    ],
  },
  {
    path: "patients",
    element: <ProtectedRoute component={PortalLayout} />,
    children: [
      {
        path: "",
        element: <Patients />,
      },
      {
        path: ":patientId/exam/:examId",
        element: <ExamReport />,
      },
      {
        path: ":patientId/progression-analysis/:progressionAnalysisId",
        element: <ProgressionAnalysisReport />,
      },
    ],
  },
  {
    path: "settings",
    element: <ProtectedRoute component={PortalLayout} />,
    children: [
      {
        path: "",
        element: <Settings />,
      },
    ],
  },
  {
    path: "knowledge-hub",
    element: <ProtectedRoute component={PortalLayout} />,
    children: [
      {
        path: "",
        element: <KnowledgeHub />,
        children: [
          { path: "tutorial-videos", element: <KnowledgeHub /> },
          { path: "data-hub-guide", element: <KnowledgeHub /> },
          {
            path: "operating-manual",
            element: <KnowledgeHub />,
            children: [
              {
                path: "publication-information",
                element: <></>,
                children: [{ path: "disclaimers", element: <></> }],
              },
              {
                path: "important-information",
                element: <></>,
                children: [
                  { path: "warnings-and-precautions", element: <></> },
                ],
              },
              {
                path: "system-description",
                element: <></>,
                children: [
                  { path: "intended-use", element: <></> },
                  { path: "usage", element: <></> },
                  { path: "results", element: <></> },
                ],
              },
              { path: "software-updates", element: <></> },
              {
                path: "features",
                element: <></>,
                children: [
                  { path: "general", element: <></> },
                  { path: "visual-field", element: <></> },
                  { path: "visual-acuity", element: <></> },
                  { path: "contrast-sensitivity", element: <></> },
                  { path: "color-vision", element: <></> },
                ],
              },
              {
                path: "head-mounted-device-controls",
                element: <></>,
                children: [
                  { path: "headset-buttons", element: <></> },
                  { path: "headset-status-indicators", element: <></> },
                  { path: "controller-buttons", element: <></> },
                  { path: "controller-status-indicators", element: <></> },
                ],
              },
              {
                path: "portal-overview",
                element: <></>,
                children: [
                  { path: "patient-page", element: <></> },
                  { path: "dashboard-page", element: <></> },
                  { path: "live-update-page", element: <></> },
                ],
              },
              {
                path: "instructions-for-use",
                element: <></>,
                children: [
                  { path: "account-registration", element: <></> },
                  { path: "configure-settings", element: <></> },
                  { path: "turning-on-the-vr-device", element: <></> },
                  { path: "create-select-patient", element: <></> },
                  { path: "create-an-exam", element: <></> },
                  { path: "launching-an-exam", element: <></> },
                  { path: "viewing-results", element: <></> },
                  { path: "progression-analysis", element: <></> },
                  { path: "external-exams", element: <></> },
                  { path: "tutorial-hub", element: <></> },
                ],
              },
              {
                path: "virtual-reality-device",
                element: <></>,
                children: [
                  { path: "set-up-wifi", element: <></> },
                  { path: "vr-device-care", element: <></> },
                  { path: "device-lifetime-expectancy", element: <></> },
                ],
              },
              {
                path: "faq",
                element: <></>,
                children: [
                  { path: "faq-new-exam", element: <></> },
                  { path: "faq-controller-visibility", element: <></> },
                  { path: "faq-room-off-centre", element: <></> },
                  { path: "faq-vr-reset", element: <></> },
                  { path: "faq-reset-password", element: <></> },
                  { path: "faq-edit-exam-settings", element: <></> },
                  { path: "faq-move-exam-patient", element: <></> },
                  { path: "faq-deviation-results", element: <></> },
                  { path: "faq-controller-not-working", element: <></> },
                  { path: "faq-exam-wont-link", element: <></> },
                  { path: "faq-turn-on-controller", element: <></> },
                  { path: "faq-battery-life", element: <></> },
                  { path: "faq-charging-overnight", element: <></> },
                  { path: "faq-add-user", element: <></> },
                  { path: "faq-headset-offline", element: <></> },
                  { path: "faq-connect-wifi", element: <></> },
                  { path: "faq-wifi-password", element: <></> },
                  { path: "faq-no-devices", element: <></> },
                  { path: "faq-prescription", element: <></> },
                  { path: "faq-glasses", element: <></> },
                  { path: "faq-lighting", element: <></> },
                  { path: "faq-headset-fitting", element: <></> },
                  { path: "faq-transfer-devices", element: <></> },
                ],
              },
              { path: "browser", element: <></> },
              { path: "security", element: <></> },
              { path: "contact-us", element: <></> },
              { path: "appendix-a", element: <></> },
              { path: "appendix-b", element: <></> },
              { path: "appendix-c", element: <></> },
              { path: "appendix-d", element: <></> },
              { path: "appendix-e", element: <></> },
              { path: "appendix-f", element: <></> },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "privacy-policy",
    element: <ProtectedRoute component={PortalLayout} />,
    children: [
      {
        path: "",
        element: <PrivacyPolicy />,
      },
    ],
  },
  {
    path: "terms-and-conditions",
    element: <ProtectedRoute component={PortalLayout} />,
    children: [
      {
        path: "",
        element: <TermsAndConditions />,
      },
    ],
  },
  {
    path: "callback",
    element: <Callback />,
  },
  {
    path: "invalid",
    element: <ProtectedRoute component={Invalid} />,
  },
  {
    path: "401",
    element: <UnauthorizedPdf />,
  },
  {
    path: "404",
    element: <ProtectedRoute component={NotFound} />,
  },
  {
    path: "*",
    element: <Navigate to="/404" />,
  },
];

export default routes;
